// ABOUTME: Responsive child profile header component displaying child avatar and name with fallback handling.
// ABOUTME: Features responsive sizing, accessibility support, and graceful degradation for missing or broken images.

import React from 'react';
import { Avatar, Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { SentimentSatisfied as SmileyIcon } from '@mui/icons-material';
import { getAvatarUrl } from '../utils/avatarUtils';

interface ChildProfileHeaderProps {
  childName: string;
  profileImageUrl?: string | null;
  size?: 'small' | 'medium' | 'large';
}

const ChildProfileHeader: React.FC<ChildProfileHeaderProps> = ({
  childName,
  profileImageUrl,
  size = 'medium'
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  // Responsive avatar sizing
  const getAvatarSize = () => {
    if (size === 'small') return 32;
    if (size === 'large') return 84;
    
    // Medium size with responsive breakpoints
    if (isMobile) return 48;
    if (isTablet) return 64;
    return 84; // Desktop
  };

  const avatarSize = getAvatarSize();

  // Convert relative URL to absolute URL using shared utility
  const absoluteImageUrl = getAvatarUrl(profileImageUrl);
  
  // Handle image loading errors
  const [imageError, setImageError] = React.useState(false);
  const [imageLoading, setImageLoading] = React.useState(Boolean(absoluteImageUrl));

  React.useEffect(() => {
    setImageError(false);
    setImageLoading(Boolean(absoluteImageUrl));
  }, [absoluteImageUrl]);

  const handleImageError = () => {
    setImageError(true);
    setImageLoading(false);
  };

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  // Clean and validate child name
  const displayName = childName?.trim() || 'Child';

  // Determine what to show in avatar
  const showFallbackIcon = !absoluteImageUrl || imageError;
  const showImage = absoluteImageUrl && !imageError;

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={2}
      role="banner"
      aria-label={`${displayName}'s profile`}
    >
      <Avatar
        src={showImage ? absoluteImageUrl : undefined}
        alt={`${displayName}'s profile picture`}
        sx={{
          width: avatarSize,
          height: avatarSize,
          bgcolor: showFallbackIcon ? 'primary.main' : 'transparent',
          transition: 'all 0.3s ease',
          opacity: imageLoading ? 0.7 : 1,
        }}
        onError={handleImageError}
        onLoad={handleImageLoad}
        imgProps={{
          onError: handleImageError,
          onLoad: handleImageLoad,
        }}
      >
        {showFallbackIcon && (
          <SmileyIcon
            sx={{
              width: avatarSize * 0.6,
              height: avatarSize * 0.6,
              color: 'primary.contrastText',
            }}
            aria-label="Default profile icon"
          />
        )}
      </Avatar>

      <Box>
        <Typography
          variant={size === 'small' ? 'h6' : size === 'large' ? 'h4' : 'h5'}
          component="h1"
          sx={{
            // variant is dynamic (h6/h4/h5 have different baseline weights); 600 is redundant for h4 but intentional emphasis for h6/h5, so it can't be classified as a single redundant/bolding case
            fontWeight: 600,
            color: 'text.primary',
            lineHeight: 1.2,
            wordBreak: 'break-word',
          }}
          aria-label={`Child name: ${displayName}`}
        >
          {displayName}
        </Typography>
        
        {size !== 'small' && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5 }}
          >
            Dashboard
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ChildProfileHeader;